import { useState, useEffect } from 'react';
import { useSettings, useUpdateSettings } from '@/api/use-settings';
import { useTags, useUpdateTag } from '@/api/use-tags';
import { useKeys, useCreateKey, useRevokeKey } from '@/api/use-keys';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CopyButton } from '@/components/shared/CopyButton';
import { Plus, Key, Plug, Terminal, BookOpen, ChevronDown, ChevronRight, ExternalLink, Download, Package, Loader2 } from 'lucide-react';
import { formatDate, cn } from '@/lib/utils';

const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Asia/Tokyo',
  'Asia/Shanghai', 'Asia/Kolkata', 'Australia/Sydney', 'Australia/Darwin',
];

const SLIDER_LABELS = ['RELAXED', 'NORMAL', 'INTENSE', 'EXPERT'];

const TAG_THEMES: Record<string, { bg: string; bar: string; emoji: string; desc: string }> = {
  life:   { bg: 'bg-amber-50 border-amber-200', bar: 'bg-amber-400', emoji: '🌿', desc: 'Essential daily vocab' },
  social: { bg: 'bg-pink-50 border-pink-200',   bar: 'bg-pink-400',  emoji: '👥', desc: 'Idioms & Slang' },
  pro:    { bg: 'bg-orange-50 border-orange-200', bar: 'bg-orange-400', emoji: '💼', desc: 'Business English' },
  default:{ bg: 'bg-blue-50 border-blue-200',    bar: 'bg-blue-400',  emoji: '📚', desc: 'General vocabulary' },
};

function getTagTheme(name: string) {
  const l = name.toLowerCase();
  if (l.includes('life') || l.includes('daily')) return TAG_THEMES.life;
  if (l.includes('social') || l.includes('slang')) return TAG_THEMES.social;
  if (l.includes('pro') || l.includes('business') || l.includes('work')) return TAG_THEMES.pro;
  return TAG_THEMES.default;
}

/* ---------- Skills Section ---------- */

interface SkillsInfo {
  version: string;
  published_at: string;
  download_url: string;
  file_name: string;
  file_size: number;
}

function SkillsSection() {
  const [skills, setSkills] = useState<SkillsInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const API_BASE = import.meta.env.VITE_API_URL ?? '';
    fetch(`${API_BASE}/api/v1/skills/latest`)
      .then(res => res.ok ? res.json() : Promise.reject(res.statusText))
      .then(data => { setSkills(data); setLoading(false); })
      .catch(err => { setError(String(err)); setLoading(false); });
  }, []);

  const handleDownload = async () => {
    if (!skills) return;
    setDownloading(true);
    try {
      const API_BASE = import.meta.env.VITE_API_URL ?? '';
      const res = await fetch(`${API_BASE}${skills.download_url}`);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = skills.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <CardTitle>Skills Package</CardTitle>
              <p className="text-sm text-muted-foreground mt-0.5">AI skill definitions for Cadence training workflows.</p>
            </div>
          </div>
          {skills && (
            <Badge className="bg-violet-100 text-violet-700 border-0 font-mono">{skills.version}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading skills info...
          </div>
        ) : error && !skills ? (
          <p className="text-sm text-muted-foreground text-center py-4">Skills package not available yet. Deploy a version tag to generate one.</p>
        ) : skills ? (
          <div className="flex items-center justify-between border rounded-lg p-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{skills.file_name}</span>
                <Badge variant="outline" className="text-xs">{formatSize(skills.file_size)}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Published {formatDate(skills.published_at)}
              </p>
            </div>
            <Button onClick={handleDownload} disabled={downloading}
              className="bg-violet-600 hover:bg-violet-700 shrink-0">
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/* ---------- MCP Guide Section ---------- */

function McpGuideSection() {
  const [expandedSection, setExpandedSection] = useState<string | null>('quickstart');

  const toggle = (key: string) => setExpandedSection(prev => prev === key ? null : key);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Plug className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <CardTitle>MCP Integration Guide</CardTitle>
              <p className="text-sm text-muted-foreground mt-0.5">Connect your AI assistant to Cadence for intelligent training sessions.</p>
            </div>
          </div>
          <Badge className="bg-indigo-100 text-indigo-700 border-0">MCP v1.0</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* What is MCP */}
        <CollapsibleBlock
          id="what-is-mcp"
          title="What is MCP?"
          icon={<BookOpen className="w-4 h-4" />}
          expanded={expandedSection === 'what-is-mcp'}
          onToggle={() => toggle('what-is-mcp')}
        >
          <p className="text-sm text-muted-foreground leading-relaxed">
            <strong>Model Context Protocol (MCP)</strong> is an open standard that allows AI assistants (like Claude) to interact with external tools and services.
            Cadence exposes <strong>37 MCP tools</strong> that let your AI assistant:
          </p>
          <ul className="text-sm text-muted-foreground mt-2 space-y-1.5 ml-4 list-disc">
            <li>Conduct interactive English training sessions with real-time feedback</li>
            <li>Run spaced-repetition reviews using the SM-2 algorithm</li>
            <li>Track new vocabulary items, stuck points, and progress scores</li>
            <li>Generate personalized study plans based on your learning data</li>
          </ul>
        </CollapsibleBlock>

        {/* Quick Start */}
        <CollapsibleBlock
          id="quickstart"
          title="Quick Start Setup"
          icon={<Terminal className="w-4 h-4" />}
          expanded={expandedSection === 'quickstart'}
          onToggle={() => toggle('quickstart')}
        >
          <div className="space-y-4">
            <Step number={1} title="Get your API endpoint">
              <p className="text-sm text-muted-foreground">Your Cadence MCP server endpoint:</p>
              <CodeBlock text={`${window.location.origin}/mcp`} copyable />
              <p className="text-xs text-muted-foreground mt-1">This is your current server address. For production, it will be your Cloudflare Worker URL.</p>
            </Step>

            <Step number={2} title="Create an API Key">
              <p className="text-sm text-muted-foreground">
                MCP connections require a valid API key for authentication. Scroll down to the <strong>API Keys</strong> section to create one, then copy the generated key.
              </p>
              <p className="text-xs text-muted-foreground mt-1.5">
                The key is shown only once — save it securely.
              </p>
            </Step>

            <Step number={3} title="Configure your AI client">
              <p className="text-sm text-muted-foreground mb-2">
                <strong>Option A:</strong> Header auth (recommended) — Add this to your AI client's MCP configuration:
              </p>
              <CodeBlock text={`{
  "mcpServers": {
    "cadence": {
      "type": "streamable-http",
      "url": "${window.location.origin}/mcp",
      "headers": {
        "Authorization": "Bearer <your-api-key>"
      }
    }
  }
}`} copyable />
              <p className="text-sm text-muted-foreground mt-3 mb-2">
                <strong>Option B:</strong> URL path auth — For clients that don't support custom headers:
              </p>
              <CodeBlock text={`{
  "mcpServers": {
    "cadence": {
      "type": "streamable-http",
      "url": "${window.location.origin}/mcp/<your-api-key>"
    }
  }
}`} copyable />
              <p className="text-xs text-muted-foreground mt-1">Replace <code className="text-xs bg-muted px-1 rounded">&lt;your-api-key&gt;</code> with the key from Step 2.</p>
            </Step>

            <Step number={4} title="Start your first session">
              <p className="text-sm text-muted-foreground">
                Once connected, simply tell your AI assistant:
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <CommandChip text="/cadence train" desc="Start a training session" />
                <CommandChip text="/cadence review" desc="Review due vocabulary" />
                <CommandChip text="/cadence warmup" desc="Quick warmup before training" />
              </div>
            </Step>
          </div>
        </CollapsibleBlock>

        {/* Available Tools */}
        <CollapsibleBlock
          id="tools"
          title="Available MCP Tools (37)"
          icon={<Terminal className="w-4 h-4" />}
          expanded={expandedSection === 'tools'}
          onToggle={() => toggle('tools')}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <ToolGroup title="Training & Queue" count={3} tools={['cards_recommended', 'queue_today', 'queue_warmup']} color="indigo" />
            <ToolGroup title="Item Management" count={10} tools={['item_add', 'item_review', 'item_list', 'item_search', 'item_update', 'item_delete', 'item_auto_create', 'item_bulk_import', 'items_due_today', 'item_stats']} color="emerald" />
            <ToolGroup title="Scenes & Cards" count={11} tools={['scene_create', 'scene_update', 'scene_delete', 'scene_group_create', 'scene_card_create', 'scene_card_list', '...5 more']} color="amber" />
            <ToolGroup title="Logs & Analytics" count={3} tools={['log_create', 'log_get_recent', 'log_get']} color="rose" />
            <ToolGroup title="Statistics" count={4} tools={['stats_overview', 'stats_scene', 'stats_retention', 'stats_progress']} color="purple" />
            <ToolGroup title="System & Config" count={6} tools={['get_instructions', 'settings_get', 'settings_update', 'tag_list', 'book_list', '...more']} color="slate" />
          </div>
        </CollapsibleBlock>

        {/* Key Workflows */}
        <CollapsibleBlock
          id="workflows"
          title="Key AI Workflows"
          icon={<BookOpen className="w-4 h-4" />}
          expanded={expandedSection === 'workflows'}
          onToggle={() => toggle('workflows')}
        >
          <div className="space-y-4">
            <WorkflowCard
              title="Training Session"
              steps={[
                'AI calls cards_recommended to pick a scene card',
                'AI calls queue_warmup to get context and stuck points',
                'Conducts interactive dialogue practice with user',
                'AI calls item_auto_create for new vocabulary discovered',
                'AI calls log_create with scores, stuck_points, and new_phrases',
              ]}
              color="indigo"
            />
            <WorkflowCard
              title="Spaced Repetition Review"
              steps={[
                'AI calls items_due_today to get overdue vocabulary',
                'Tests each item with user in context',
                'AI calls item_review with quality score (0-5) per item',
                'AI calls log_create with item_results for the session',
                'SM-2 algorithm automatically schedules next review dates',
              ]}
              color="rose"
            />
          </div>
        </CollapsibleBlock>

        {/* Learn More */}
        <div className="flex items-center gap-4 pt-2 text-sm">
          <a href="https://modelcontextprotocol.io" target="_blank" rel="noreferrer"
            className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 font-medium">
            MCP Documentation <ExternalLink className="w-3 h-3" />
          </a>
          <a href="https://github.com/anthropics/anthropic-cookbook" target="_blank" rel="noreferrer"
            className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 font-medium">
            MCP Examples <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------- Sub-components ---------- */

function CollapsibleBlock({ id: _id, title, icon, expanded, onToggle, children }: {
  id?: string; title: string; icon: React.ReactNode; expanded: boolean; onToggle: () => void; children: React.ReactNode;
}) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors text-left">
        <span className="text-muted-foreground">{icon}</span>
        <span className="font-medium text-sm flex-1">{title}</span>
        {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
      </button>
      {expanded && <div className="px-4 pt-4 pb-4 border-t bg-muted/10">{children}</div>}
    </div>
  );
}

function Step({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
        {number}
      </div>
      <div className="flex-1">
        <h4 className="text-sm font-semibold mb-1">{title}</h4>
        {children}
      </div>
    </div>
  );
}

function CodeBlock({ text, copyable }: { text: string; copyable?: boolean }) {
  return (
    <div className="bg-slate-900 text-slate-100 rounded-lg p-3 mt-2 relative group">
      <pre className="text-xs overflow-x-auto whitespace-pre-wrap font-mono">{text}</pre>
      {copyable && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <CopyButton text={text} label="Copy" />
        </div>
      )}
    </div>
  );
}

function CommandChip({ text, desc }: { text: string; desc: string }) {
  return (
    <div className="flex items-center gap-2 bg-muted/50 border rounded-lg px-3 py-2">
      <code className="text-xs font-mono font-semibold text-indigo-600">{text}</code>
      <span className="text-xs text-muted-foreground">{desc}</span>
    </div>
  );
}

function ToolGroup({ title, count, tools, color }: { title: string; count: number; tools: string[]; color: string }) {
  const colorMap: Record<string, string> = {
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <div className={cn('border rounded-lg p-3', colorMap[color] ?? colorMap.slate)}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold">{title}</span>
        <Badge variant="secondary" className="text-[10px] h-5">{count}</Badge>
      </div>
      <div className="flex flex-wrap gap-1">
        {tools.map(t => (
          <code key={t} className="text-[10px] bg-white/60 rounded px-1.5 py-0.5 font-mono">{t}</code>
        ))}
      </div>
    </div>
  );
}

function WorkflowCard({ title, steps, color }: { title: string; steps: string[]; color: string }) {
  return (
    <div className="border rounded-lg p-4">
      <h4 className={cn('text-sm font-bold mb-3', color === 'indigo' ? 'text-indigo-700' : 'text-rose-700')}>{title}</h4>
      <ol className="space-y-2">
        {steps.map((step, i) => (
          <li key={i} className="flex gap-2.5 text-sm text-muted-foreground">
            <span className={cn(
              'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5',
              color === 'indigo' ? 'bg-indigo-100 text-indigo-600' : 'bg-rose-100 text-rose-600',
            )}>{i + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ---------- Main Settings ---------- */

export default function Settings() {
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();
  const { data: tags } = useTags();
  const updateTag = useUpdateTag();
  const { data: keys } = useKeys();
  const createKey = useCreateKey();
  const revokeKey = useRevokeKey();

  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyResult, setNewKeyResult] = useState<{ key_id: string; plain_key: string } | null>(null);

  const dailyCards = parseInt(settings?.daily_card_count ?? '5', 10);
  const timezone = settings?.timezone ?? 'UTC';
  const activeTags = (tags ?? []).filter(t => t.is_active === 1);
  const totalWeight = activeTags.reduce((sum, t) => sum + t.weight, 0) || 1;

  const sliderLabel = dailyCards <= 2 ? SLIDER_LABELS[0]
    : dailyCards <= 5 ? SLIDER_LABELS[1]
    : dailyCards <= 8 ? SLIDER_LABELS[2] : SLIDER_LABELS[3];

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;
    const result = await createKey.mutateAsync({ name: newKeyName.trim() });
    setNewKeyResult(result.data);
    setNewKeyName('');
  };

  return (
    <div className="space-y-8">
      {/* Skills Package */}
      <SkillsSection />

      {/* MCP Integration Guide */}
      <McpGuideSection />

      {/* Training Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Training Settings</CardTitle>
          <p className="text-sm text-muted-foreground">Configure your daily learning velocity and temporal preferences.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-semibold">Daily Recommended Cards</label>
                <span className="text-2xl font-bold">{dailyCards} <span className="text-sm font-normal text-muted-foreground">units</span></span>
              </div>
              <Slider min={1} max={10} step={1} value={[dailyCards]}
                onValueChange={([val]) => updateSettings.mutate({ daily_card_count: String(val) })} />
              <div className="flex justify-between text-[10px] text-muted-foreground mt-2 uppercase tracking-wider">
                {SLIDER_LABELS.map(l => (
                  <span key={l} className={cn(l === sliderLabel && 'text-primary font-bold')}>{l}</span>
                ))}
              </div>
            </div>
            <div className="border rounded-lg p-4">
              <label className="text-sm font-semibold block mb-3">System Timezone</label>
              <Select value={timezone} onValueChange={val => updateSettings.mutate({ timezone: val })}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">Daily streaks reset at 4:00 AM in your local time.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tag Weights */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Tag Weights</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">Prioritize specific vocabulary domains in your spaced repetition queue.</p>
            </div>
            <Badge variant="outline" className="text-red-500 border-red-200 gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> LIVE OPTIMIZATION
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {activeTags.length > 0 && (
            <div className="flex rounded-lg overflow-hidden h-8">
              {activeTags.map(tag => {
                const pct = Math.round((tag.weight / totalWeight) * 100);
                return (
                  <div key={tag.id} className={cn('flex items-center justify-center text-xs font-bold text-white', getTagTheme(tag.name).bar)}
                    style={{ width: `${pct}%` }}>
                    {tag.name.toUpperCase()} {pct}%
                  </div>
                );
              })}
            </div>
          )}

          {(tags ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No tags yet</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(tags ?? []).map(tag => {
                const theme = getTagTheme(tag.name);
                return (
                  <div key={tag.id} className={cn('border rounded-lg p-4', theme.bg)}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{theme.emoji}</span>
                        <div>
                          <p className="font-semibold text-sm">{tag.name}</p>
                          <p className="text-[10px] text-muted-foreground">{theme.desc}</p>
                        </div>
                      </div>
                      <Switch checked={tag.is_active === 1}
                        onCheckedChange={checked => updateTag.mutate({ id: tag.id, is_active: checked ? 1 : 0 })} />
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                      <span>Priority Weight</span>
                      <span className="font-semibold text-foreground">{tag.weight}/100</span>
                    </div>
                    <Slider min={0} max={100} step={5} value={[tag.weight]}
                      onValueChange={([val]) => updateTag.mutate({ id: tag.id, weight: val })}
                      disabled={tag.is_active === 0} />
                    <div className="flex items-center gap-1.5 mt-3">
                      <div className={cn('w-4 h-4 rounded', theme.bar)} />
                      <span className="text-xs text-muted-foreground">Theme {tag.name.includes('Life') ? 'Primary' : tag.name.includes('Social') ? 'Secondary' : 'Tertiary'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* API Keys */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Key className="w-4 h-4" /> API Keys</CardTitle>
          <p className="text-sm text-muted-foreground">Manage external integrations and developer access.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input placeholder="Key name (e.g. Anki Sync Integration)" value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateKey()} />
            <Button onClick={handleCreateKey} disabled={!newKeyName.trim() || createKey.isPending}
              className="bg-red-500 hover:bg-red-600 shrink-0">
              <Plus className="w-4 h-4 mr-2" /> Create New Key
            </Button>
          </div>

          {newKeyResult && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm font-medium text-amber-800 mb-1">Copy your new API key now — it won't be shown again.</p>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-white border rounded px-2 py-1 flex-1 truncate">{newKeyResult.plain_key}</code>
                <CopyButton text={newKeyResult.plain_key} label="Copy" />
              </div>
            </div>
          )}

          {(keys ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No API keys yet</p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase text-xs tracking-wider">Key Name</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase text-xs tracking-wider">Created</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase text-xs tracking-wider">Last Used</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase text-xs tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase text-xs tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(keys ?? []).map(key => (
                    <tr key={key.key_id} className="border-t">
                      <td className="px-4 py-3 font-medium flex items-center gap-2">
                        <span className="w-6 h-6 rounded bg-muted flex items-center justify-center"><Key className="w-3.5 h-3.5 text-muted-foreground" /></span>
                        {key.name}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{formatDate(key.created_at)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{key.last_used_at ? formatDate(key.last_used_at) : 'Never'}</td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary" className={key.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                          {key.is_active ? '● ACTIVE' : 'REVOKED'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {key.is_active && (
                          <button onClick={() => revokeKey.mutate(key.key_id)} className="text-sm text-red-500 hover:text-red-700 font-medium">Revoke</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
